import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DateFormats } from 'src/app/shared/enums/date-formats.enum';
import { FdFieldConfigs } from 'src/app/shared/fd-form-components/fd-form-components.module';
import { FdDatepickerConfig } from 'src/app/shared/fd-form-components/fd-datepicker/fd-datepicker.component';
import { FdInputConfig, TransformType } from 'src/app/shared/fd-form-components/fd-input/fd-input.component';
import { DateMask } from 'src/app/shared/masks/date-mask';
import { cpfCnpjMask } from 'src/app/shared/masks/document-masks';
import { OnlyNumberMask } from 'src/app/shared/masks/only-number-mask';
import { DateCompareValidator } from 'src/app/shared/validators/date-compare-validator';
import { CompareTypeEnum } from 'src/app/shared/enums/compare-type.enum';
import { Messages } from 'src/app/shared/messages/messages';
import { HttpErrorResponse } from '@angular/common/http';
import { DialogService } from 'src/app/shared/service/dialog.service';
import { finalize } from 'rxjs/operators';
import { LoadingService } from 'src/app/shared/service/loading.service';
import { ErrorService } from 'src/app/shared/service/error.service';
import { FormValidationService } from 'src/app/shared/service/form-validation.service';
import { CnpjValidator } from 'src/app/shared/validators/cpf-cnpj-validator';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { formatDateOnlyToIso, normalizeToDateOnly } from 'src/app/shared/utils/date-only.utils';
import { ReleaseOfUnwantedCnaeService } from '../../services/release-of-unwanted-cnae.service';
import { ReleaseOfUnwantedCnaeCreateModel, ReleaseOfUnwantedCnaeModel, ReleaseOfUnwantedCnaeUpdateModel } from '../../models/releaseOfUnwantedCnae.models';

@Component({
  selector: 'app-add-or-edit-release-of-unwanted-cnae',
  standalone: false,
  templateUrl: './add-or-edit-release-of-unwanted-cnae.component.html',
  styleUrl: './add-or-edit-release-of-unwanted-cnae.component.scss'
})
export class AddOrEditReleaseOfUnwantedCnaeComponent implements OnInit {
  formGroup: UntypedFormGroup = new UntypedFormGroup({});
  fields: FdFieldConfigs = {};
  selectedInstitution: string = '';
  readonly editionModal: boolean;
  readonly releaseOfUnwantedCnae?: ReleaseOfUnwantedCnaeModel;

  get cnpjField(): FdInputConfig {
    return this.fields.cnpj as FdInputConfig;
  }

  get clientNameField(): FdInputConfig {
    return this.fields.clientName as FdInputConfig;
  }

  get cnaeField(): FdInputConfig {
    return this.fields.cnae as FdInputConfig;
  }

  get startDateField(): FdDatepickerConfig {
    return this.fields.initialDate as FdDatepickerConfig;
  }

  get endDateField(): FdDatepickerConfig {
    return this.fields.finalDate as FdDatepickerConfig;
  }

  constructor(
    readonly formBuilder: UntypedFormBuilder,
    readonly loadingService: LoadingService,
    readonly dialogService: DialogService,
    readonly errorService: ErrorService,
    readonly formValidationService: FormValidationService,
    readonly dialogRef: MatDialogRef<AddOrEditReleaseOfUnwantedCnaeComponent>,
    readonly releaseOfUnwantedCnaeService: ReleaseOfUnwantedCnaeService,
    @Inject(MAT_DIALOG_DATA) data?: {
      editionModal?: boolean;
      releaseOfUnwantedCnae?: ReleaseOfUnwantedCnaeModel;
    }
  ) {
    this.editionModal = !!data?.editionModal;
    this.releaseOfUnwantedCnae = data?.releaseOfUnwantedCnae;
  }

  ngOnInit(): void {
    this.startForms();
    this.loadEditionData();
    this.setDateCompareValidators();
  }

  changeSelectedInstitution(institution: string) {
    this.selectedInstitution = institution;
  }

  setDateCompareValidators() {
    this.formGroup.controls.initialDate.addValidators(
      DateCompareValidator(this.formGroup.controls.finalDate, CompareTypeEnum.LESS_THAN_OR_EQUAL)
    );

    this.formGroup.controls.finalDate.addValidators(
      DateCompareValidator(this.formGroup.controls.initialDate, CompareTypeEnum.GREATER_THAN_OR_EQUAL)
    );
  }

  startForms(): void {

    this.formGroup = this.formBuilder.group({
      institution: ['', Validators.required],
      serviceContract: ['', Validators.required],
      cnpj: ['', [Validators.required, CnpjValidator]],
      clientName: ['', Validators.required],
      cnae: ['', Validators.required],
      initialDate: ['', Validators.required],
      finalDate: ['', Validators.required]
    });

    this.fields = {
      institution: {
        label: 'Nº Instituição',
        items: [],
        emptyOptionLabel: 'Selecione uma opção',
        controlName: 'institution',
        maskCharsReplace: /[. / -]/g,
        panelWidth: '320px',
        messages: {
          required: 'Informe uma instituição',
          invalid: 'Usuário inválido'
        }
      },
      serviceContract: {
        label: 'Service Contract',
        items: [],
        emptyOptionLabel: 'Selecione uma opção',
        controlName: 'serviceContract',
        maskCharsReplace: /[. / -]/g,
        panelWidth: '320px',
        messages: {
          required: 'Informe um service contract',
          invalid: 'Usuário inválido'
        }
      },
      cnpj: {
        label: 'CNPJ',
        controlName: 'cnpj',
        maskCharsReplace: /[.\/ -]/g,
        transform: TransformType.UPPERCASE,
        mask: cpfCnpjMask,
        messages: {
          required: 'Informe o CNPJ',
          invalidCnpj: 'CNPJ inválido'
        }
      },
      clientName: {
        label: 'Nome',
        controlName: 'clientName',
        maxLength: 150,
        messages: {
          required: 'Informe o nome do cliente',
        }
      },
      cnae: {
        label: 'CNAE',
        controlName: 'cnae',
        mask: OnlyNumberMask,
        maxLength: 7,
        messages: {
          required: 'Informe o CNAE'
        }
      },
      initialDate: {
        label: 'Data inicial',
        items: [],
        valueFormat: DateFormats.YEAR_MONTH_DAY_WITH_HYPHEN,
        mask: DateMask,
        controlName: 'initialDate',
        messages: {
          required: 'Informe uma data',
          lessThanOrEqual: 'A data inicial deve ser menor ou igual a data final'
        }
      },
      finalDate: {
        label: 'Data final',
        items: [],
        valueFormat: DateFormats.YEAR_MONTH_DAY_WITH_HYPHEN,
        mask: DateMask,
        controlName: 'finalDate',
        messages: {
          required: 'Informe uma data',
          greaterThanOrEqual: 'A data final deve ser maior ou igual a data inicial'
        }
      }
    };
  }

  sanitizeCnae(value: string): void {
    const sanitizedValue = (value || '').replace(/\D/g, '');

    if (this.formGroup.controls.cnae.value !== sanitizedValue) {
      this.formGroup.controls.cnae.setValue(sanitizedValue);
    }
  }

  private loadEditionData(): void {
    if (!this.editionModal || !this.releaseOfUnwantedCnae) {
      return;
    }

    this.formGroup.patchValue({
      institution: this.releaseOfUnwantedCnae.institution,
      serviceContract: this.releaseOfUnwantedCnae.serviceContract,
      cnpj: this.releaseOfUnwantedCnae.cnpj,
      clientName: this.releaseOfUnwantedCnae.clientName,
      cnae: this.releaseOfUnwantedCnae.cnae,
      initialDate: normalizeToDateOnly(this.releaseOfUnwantedCnae.initialDate),
      finalDate: normalizeToDateOnly(this.releaseOfUnwantedCnae.finalDate)
    });

    this.formGroup.controls.institution.disable({ emitEvent: false });
    this.formGroup.controls.serviceContract.disable({ emitEvent: false });

    if (this.releaseOfUnwantedCnae.institution) {
      this.selectedInstitution = this.releaseOfUnwantedCnae.institution;
    }
  }

  private getCreationPayload(): ReleaseOfUnwantedCnaeCreateModel {
    const payload: ReleaseOfUnwantedCnaeCreateModel = {
      institution: this.formGroup.value.institution || null,
      serviceContract: this.formGroup.value.serviceContract || null,
      cnpj: this.formGroup.value.cnpj || null,
      clientName: this.formGroup.value.clientName || null,
      cnae: this.formGroup.value.cnae || null,
      initialDate: formatDateOnlyToIso(this.formGroup.value.initialDate),
      finalDate: formatDateOnlyToIso(this.formGroup.value.finalDate)
    };
    return payload;
  }

  private getUpdatePayload(): ReleaseOfUnwantedCnaeUpdateModel {
    const payload: ReleaseOfUnwantedCnaeUpdateModel = {
      id: this.releaseOfUnwantedCnae?.id || 0,
      cnpj: this.formGroup.value.cnpj || null,
      clientName: this.formGroup.value.clientName || null,
      cnae: this.formGroup.value.cnae || null,
      initialDate: formatDateOnlyToIso(this.formGroup.value.initialDate),
      finalDate: formatDateOnlyToIso(this.formGroup.value.finalDate)
    };
    return payload;
  }

  private checkInvalidDate(): void {
    const initialDateControl = this.formGroup.controls.initialDate;
    const finalDateControl = this.formGroup.controls.finalDate;

    const initialDate = normalizeToDateOnly(initialDateControl.value);
    const finalDate = normalizeToDateOnly(finalDateControl.value);

    if (initialDateControl.hasError('lessThanOrEqual')) {
      delete initialDateControl.errors?.lessThanOrEqual;
      initialDateControl.updateValueAndValidity({ emitEvent: false });
    }

    if (finalDateControl.hasError('greaterThanOrEqual')) {
      delete finalDateControl.errors?.greaterThanOrEqual;
      finalDateControl.updateValueAndValidity({ emitEvent: false });
    }

    if (!initialDate || !finalDate) {
      return;
    }

    if (initialDate.getTime() > finalDate.getTime()) {
      if (initialDateControl.errors) {
        initialDateControl.setErrors({ ...initialDateControl.errors, lessThanOrEqual: true });
      } else {
        initialDateControl.setErrors({ lessThanOrEqual: true });
      }
      initialDateControl.markAsTouched();

      if (finalDateControl.errors) {
        finalDateControl.setErrors({ ...finalDateControl.errors, greaterThanOrEqual: true });
      } else {
        finalDateControl.setErrors({ greaterThanOrEqual: true });
      }
      finalDateControl.markAsTouched();
    }
  }

  save() {
    this.checkInvalidDate();

    if (!this.formValidationService.isFormGroupValid(this.formGroup)) {
      return;
    }
    this.loadingService.show();

    this.releaseOfUnwantedCnaeService.save(this.getCreationPayload())
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe(() => {
        this.dialogService.openDialog(Messages.EDIT_SAVE_SUCCESS, () => this.close(true));
      }, (err: HttpErrorResponse) => this.errorService.handleXHRError(err, Messages.EDIT_SAVE_ERROR, () => this.close()));
  }

  update() {
    this.checkInvalidDate();

    if (!this.formValidationService.isFormGroupValid(this.formGroup)) {
      return;
    }
    this.loadingService.show();

    this.releaseOfUnwantedCnaeService.update(this.getUpdatePayload())
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe(() => {
        this.dialogService.openDialog(Messages.EDIT_SAVE_SUCCESS, () => this.close(true));
      }, (err: HttpErrorResponse) => this.errorService.handleXHRError(err, Messages.EDIT_SAVE_ERROR, () => this.close()));
  }

  close(updated: boolean = false) {
    this.dialogRef.close(updated);
  }
}
